import { useCallback, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { PickingInfo } from 'deck.gl';
import useDmaMode from './useDmaMode';
import type { DashboardContextValueInterface } from '@/contexts/DashboardContext';

interface UseDmaOverlayParams {
	selectedLocation: string;
	selectedDate: DashboardContextValueInterface['selectedDate'];
	onHover: (info: PickingInfo) => void;
}

export function useDmaOverlay({ selectedLocation, selectedDate, onHover }: UseDmaOverlayParams) {
	const [dmaEnabled, setDmaEnabled] = useState<boolean>(false);
	const [dmaResetKey, setDmaResetKey] = useState<number>(0);

	const isDmaDateRangeValid = useMemo(() => {
		if (!selectedDate || selectedDate.length !== 2) return true;
		const startDate = dayjs(selectedDate[0]);
		const endDate = dayjs(selectedDate[1]);
		return endDate.diff(startDate, 'day', true) >= 7;
	}, [selectedDate]);

	const isDmaOverlayEnabled = dmaEnabled && isDmaDateRangeValid;

	const { layers: dmaLayers, panelElement: dmaPanel, onMapClick: dmaOnMapClick } = useDmaMode({
		enabled: isDmaOverlayEnabled,
		selectedLocation,
		selectedDate,
		resetKey: dmaResetKey,
		onHover,
	});

	const onSetMode = useCallback((mode: string) => {
		setDmaEnabled(mode === 'dma');
	}, []);

	const onResetDma = useCallback(() => {
		setDmaResetKey((k) => k + 1);
	}, []);

	return { isDmaOverlayEnabled, dmaLayers, dmaPanel, dmaOnMapClick, onSetMode, onResetDma };
}
